import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { formatVND } from "../../lib/utils";

/** Trả về giá trị nếu có, ngược lại hiển thị "Đang cập nhật" */
function dc(value, fallback = "Đang cập nhật") {
  if (value === null || value === undefined || String(value).trim() === "") return fallback;
  return value;
}

/**
 * VietQR-style payload for VietQR. Using simple QR with bank+account+amount payload.
 * Format reference: https://vietqr.io/danh-sach-api
 * For simplicity we use the VietQR public image API: https://img.vietqr.io/image/{bank}-{account}-compact.png?amount=...
 * But to avoid external image, we encode a static text or use the bank's VietQR URL.
 */
function buildVietQRUrl(bankName, account, amount, holder, note) {
  if (!bankName || !account) return null;
  // Try to detect common bank short codes for VietQR
  const map = {
    "vietcombank": "VCB", "vcb": "VCB",
    "techcombank": "TCB", "tcb": "TCB",
    "bidv": "BIDV",
    "vietinbank": "ICB", "icb": "ICB",
    "agribank": "VBA", "vba": "VBA",
    "mbbank": "MB", "mb": "MB",
    "acb": "ACB",
    "tpbank": "TPB", "tpb": "TPB",
    "vpbank": "VPB", "vpb": "VPB",
    "sacombank": "STB", "stb": "STB",
    "hdbank": "HDB", "hdb": "HDB",
    "ocb": "OCB",
    "msb": "MSB",
    "shb": "SHB",
    "vib": "VIB",
    "lpbank": "LPB", "lpb": "LPB",
    "eximbank": "EIB", "eib": "EIB",
  };
  const low = (bankName || "").toLowerCase().replace(/\s+/g, "");
  const code = map[low] || bankName.replace(/\s+/g, "").toUpperCase();
  const amt = amount ? `&amount=${Math.round(amount)}` : "";
  const addInfo = note ? `&addInfo=${encodeURIComponent(note)}` : "";
  const acc = holder ? `&accountName=${encodeURIComponent(holder)}` : "";
  return `https://img.vietqr.io/image/${code}-${account}-compact2.png?${amt.slice(1)}${addInfo}${acc}`;
}

function VietQRImage({ shop, amount, note, size = 140 }) {
  const url = buildVietQRUrl(shop.bank_name, shop.bank_account, amount, shop.bank_account_holder, note);
  if (!url) return null;
  return (
    <img
      src={url}
      alt="QR thanh toán"
      crossOrigin="anonymous"
      style={{ width: size, height: size, objectFit: "contain", border: "1px solid #E4E4E7", padding: 2, background: "#fff" }}
    />
  );
}

function ShopHeader({ shop, variant = "a4" }) {
  const big = variant === "a4" || variant === "a5";
  const accent = shop.bill_accent_color || "#2D4A22";
  const pos = shop.bill_logo_position || "left";
  const justify = pos === "center" ? "center" : pos === "right" ? "flex-end" : "flex-start";
  return (
    <div style={{ display: "flex", justifyContent: justify, alignItems: "center", gap: 12, marginBottom: big ? 16 : 6 }}>
      {shop.bill_show_logo && shop.logo_url && (
        <img src={shop.logo_url} alt="" style={{ width: big ? 56 : 36, height: big ? 56 : 36, objectFit: "contain" }} />
      )}
      <div style={{ textAlign: pos === "center" ? "center" : "left" }}>
        <div style={{ fontSize: big ? 24 : 13, fontWeight: 800, color: accent }}>{shop.shop_name}</div>
        <div style={{ fontSize: big ? 13 : 9, color: "#52525B", lineHeight: 1.4 }}>
          {shop.bill_show_address && shop.address ? <div>{shop.address}</div> : null}
          {shop.bill_show_phone && shop.phone ? <span>ĐT: {shop.phone} </span> : null}
          {shop.bill_show_email && shop.email ? <span>· {shop.email} </span> : null}
          {shop.bill_show_website && shop.website ? <span>· {shop.website}</span> : null}
          {shop.bill_show_tax_id && shop.tax_id ? <div>MST: {shop.tax_id}</div> : null}
        </div>
      </div>
    </div>
  );
}

/** Fixed notes block (rendered above signatures) */
function FixedNotesBlock({ shop, fontSize = 13 }) {
  const notes = (shop.bill_fixed_notes || []).filter((s) => s && s.trim());
  if (notes.length === 0) return null;
  return (
    <div style={{ marginTop: 12, fontSize, color: "#52525B", lineHeight: 1.6 }}>
      {notes.map((n, i) => (
        <div key={i}>• {n}</div>
      ))}
    </div>
  );
}

/** Signatures block (4 columns: Customer / Shipper / Warehouse / Accountant) */
function SignaturesBlock({ shop, fontSize = 12 }) {
  const boxes = [
    { key: "bill_signature_customer", label: "Khách hàng" },
    { key: "bill_signature_shipper", label: "NV giao hàng" },
    { key: "bill_signature_warehouse", label: "QL Kho" },
    { key: "bill_signature_accountant", label: "Kế toán" },
  ].filter((b) => shop[b.key]);
  if (boxes.length === 0) return null;
  return (
    <div style={{ marginTop: 16, display: "flex", justifyContent: "space-around", gap: 8 }}>
      {boxes.map((b) => (
        <div key={b.key} style={{ flex: 1, textAlign: "center" }}>
          <div style={{ fontSize, fontWeight: 700 }}>{b.label}</div>
          <div style={{ fontSize: fontSize - 1, fontStyle: "italic", color: "#71717A", marginTop: 2 }}>(Ký và ghi rõ họ tên)</div>
          <div style={{ height: 44, borderBottom: "1px dotted #9CA3AF", marginTop: 2 }}></div>
        </div>
      ))}
    </div>
  );
}

/** 80mm thermal — minimal, just customer info + items + totals + dates + optional QR */
export function BillThermal({ order, shop }) {
  // Nếu cài đặt 2 liên → render 2 bản, liên 2 có nhãn riêng
  const copies = shop?.bill_print_copies || 1;
  if (copies >= 2) {
    return (
      <>
        <BillThermalSingle order={order} shop={shop} copyLabel={null} />
        <div style={{ borderTop: "2px dashed #ccc", margin: "6px 0", pageBreakBefore: "always" }} />
        <BillThermalSingle order={order} shop={shop} copyLabel={shop.bill_copy2_label || "LIÊN KHÁCH"} />
      </>
    );
  }
  return <BillThermalSingle order={order} shop={shop} copyLabel={null} />;
}

function BillThermalSingle({ order, shop, copyLabel }) {
  if (!order) return null;
  shop = shop || {};
  const purchaseDate = new Date(order.created_at);
  const printDate = new Date();

  return (
    <div className="thermal-80mm" style={{ padding: "4mm" }}>
      {copyLabel && (
        <div style={{ textAlign: "center", fontWeight: 700, fontSize: 13, letterSpacing: 1,
          border: "1px dashed #999", borderRadius: 4, padding: "3px 6px", marginBottom: 6 }}>
          — {copyLabel} —
        </div>
      )}
      <div style={{ textAlign: "center", marginBottom: 6 }}>
        {shop.bill_show_logo && shop.logo_url && (
          <img src={shop.logo_url} alt="" style={{ width: 40, height: 40, objectFit: "contain", marginBottom: 4 }} />
        )}
        <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: 0.5 }}>
          {dc(shop.shop_name)}
        </div>
        {shop.bill_show_address && <div style={{ fontSize: 12 }}>{dc(shop.address)}</div>}
        {shop.bill_show_phone && <div style={{ fontSize: 12 }}>ĐT: {dc(shop.phone)}</div>}
        <div style={{ fontSize: 12, marginTop: 4 }}>{shop.bill_invoice_title || "HÓA ĐƠN BÁN HÀNG"}</div>
      </div>

      <div className="divider" />

      <div>
        <div className="row"><span>Mã đơn:</span><strong>{order.order_code}</strong></div>
        <div className="row"><span>Khách:</span><strong style={{ maxWidth: "60%", textAlign: "right", fontSize: 17 }}>{order.customer_name}</strong></div>
        {order.customer_phone && <div className="row"><span>SĐT:</span><span>{order.customer_phone}</span></div>}
        {order.customer_address && <div style={{ marginTop: 2 }}>ĐC: {order.customer_address}</div>}
      </div>

      <div className="divider" />

      <table style={{ width: "100%", fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left" }}>Tên hàng</th>
            <th style={{ textAlign: "center", width: "12mm" }}>SL</th>
            <th style={{ textAlign: "right", width: "22mm" }}>Thành tiền</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((it, i) => (
            <React.Fragment key={i}>
              <tr><td colSpan={3} style={{ paddingTop: 3, fontWeight: 600 }}>{it.name}</td></tr>
              <tr>
                <td style={{ paddingLeft: 4 }}>{formatVND(it.price)}</td>
                <td style={{ textAlign: "center" }}>{it.quantity}</td>
                <td style={{ textAlign: "right" }}>{formatVND(it.subtotal)}</td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>

      <div className="divider" />

      <div className="row"><span>Tạm tính</span><span>{formatVND(order.subtotal)}</span></div>
      {shop.bill_show_discount !== false && (order.discount > 0 || order.discount_percent > 0) && (
        <div className="row">
          <span>Giảm{order.discount_percent > 0 ? ` (${order.discount_percent}%)` : ""}</span>
          <span>- {formatVND(order.discount_amount || order.discount)}</span>
        </div>
      )}
      {shop.bill_show_shipping !== false && order.shipping_fee > 0 && <div className="row"><span>Phí ship</span><span>+ {formatVND(order.shipping_fee)}</span></div>}
      <div className="divider" />
      <div className="row" style={{ fontSize: 13, fontWeight: 700 }}>
        <span>TỔNG</span><span>{formatVND(order.total)}</span>
      </div>

      <div className="divider" />

      <div style={{ fontSize: 12, lineHeight: 1.4 }}>
        <div>Ngày mua: {purchaseDate.toLocaleString("vi-VN")}</div>
        <div>Ngày in: {printDate.toLocaleString("vi-VN")}</div>
      </div>

      {shop.bill_show_bank_qr && shop.bank_account && shop.bank_name && (
        <>
          <div className="divider" />
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>QUÉT QR ĐỂ THANH TOÁN</div>
            <VietQRImage shop={shop} amount={order.total} note={order.order_code} size={120} />
            <div style={{ fontSize: 12, marginTop: 4 }}>{shop.bank_name} · {shop.bank_account}</div>
            {shop.bank_account_holder && <div style={{ fontSize: 12 }}>{shop.bank_account_holder}</div>}
          </div>
        </>
      )}

      <div style={{ textAlign: "center", marginTop: 8, fontSize: 12, fontStyle: "italic" }}>
        — {shop.bill_footer_text || "Cảm ơn quý khách"} —
      </div>
    </div>
  );
}

/** A4 portrait invoice */
export function InvoiceA4({ order, shop }) {
  if (!order) return null;
  shop = shop || {};
  const purchaseDate = new Date(order.created_at);
  const printDate = new Date();

  return (
    <div className="invoice-a4" style={{ padding: "14mm" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <ShopHeader shop={shop} variant="a4" />
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 21, fontWeight: 700, letterSpacing: 1 }}>{shop.bill_invoice_title || "HÓA ĐƠN"}</div>
          <div style={{ fontSize: 17, fontFamily: "Times New Roman, serif", marginTop: 4 }}>{order.order_code}</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20, fontSize: 17 }}>
        <div>
          <div style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: 1, color: "#71717A", marginBottom: 4 }}>Khách hàng</div>
          <div style={{ fontWeight: 700, fontSize: 23 }}>{order.customer_name}</div>
          {order.customer_phone && <div style={{ fontSize: 17 }}>{order.customer_phone}</div>}
          {order.customer_address && <div style={{ fontSize: 17 }}>{order.customer_address}</div>}
        </div>
        <div>
          <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1, color: "#71717A", marginBottom: 4 }}>Thông tin</div>
          <div>Ngày mua: <strong>{purchaseDate.toLocaleDateString("vi-VN")}</strong></div>
          <div>Giờ mua: {purchaseDate.toLocaleTimeString("vi-VN")}</div>
          <div>Ngày in: {printDate.toLocaleString("vi-VN")}</div>
        </div>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16, fontSize: 17 }}>
        <thead>
          <tr style={{ background: "#F4F1EA", color: "#52525B", textTransform: "uppercase", fontSize: 12, letterSpacing: 0.5 }}>
            <th style={{ padding: "10px 8px", textAlign: "left" }}>Tên hàng</th>
            <th style={{ padding: "10px 8px", textAlign: "right", width: "20%" }}>Đơn giá</th>
            <th style={{ padding: "10px 8px", textAlign: "center", width: "12%" }}>SL</th>
            <th style={{ padding: "10px 8px", textAlign: "right", width: "20%" }}>Thành tiền</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((it, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #E4E4E7" }}>
              <td style={{ padding: "10px 8px" }}>{it.name}</td>
              <td style={{ padding: "10px 8px", textAlign: "right", fontFamily: "Times New Roman, serif" }}>{formatVND(it.price)}</td>
              <td style={{ padding: "10px 8px", textAlign: "center" }}>{it.quantity}</td>
              <td style={{ padding: "10px 8px", textAlign: "right", fontFamily: "Times New Roman, serif", fontWeight: 600 }}>{formatVND(it.subtotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start" }}>
        {shop.bill_show_bank_qr && shop.bank_account && shop.bank_name && (
          <div style={{ textAlign: "center", paddingTop: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "#71717A", marginBottom: 6 }}>
              Quét QR để thanh toán
            </div>
            <VietQRImage shop={shop} amount={order.total} note={order.order_code} size={140} />
            <div style={{ fontSize: 13, marginTop: 6 }}>{shop.bank_name} · {shop.bank_account}</div>
            {shop.bank_account_holder && <div style={{ fontSize: 12 }}>{shop.bank_account_holder}</div>}
          </div>
        )}

        <div style={{ marginLeft: "auto", width: "55%", fontSize: 17 }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
            <span style={{ color: "#52525B" }}>Tạm tính</span>
            <span style={{ fontFamily: "Times New Roman, serif" }}>{formatVND(order.subtotal)}</span>
          </div>
          {(order.discount > 0 || order.discount_percent > 0) && (
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
              <span style={{ color: "#52525B" }}>Giảm giá{order.discount_percent > 0 ? ` (${order.discount_percent}%)` : ""}</span>
              <span style={{ fontFamily: "Times New Roman, serif" }}>- {formatVND(order.discount_amount || order.discount)}</span>
            </div>
          )}
          {shop.bill_show_shipping !== false && order.shipping_fee > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
              <span style={{ color: "#52525B" }}>Phí vận chuyển</span>
              <span style={{ fontFamily: "Times New Roman, serif" }}>+ {formatVND(order.shipping_fee)}</span>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 4px", borderTop: `2px solid ${shop.bill_accent_color || "#2D4A22"}`, marginTop: 6, fontSize: 20, fontWeight: 700 }}>
            <span>TỔNG CỘNG</span>
            <span style={{ color: shop.bill_accent_color || "#2D4A22", fontFamily: "Times New Roman, serif" }}>{formatVND(order.total)}</span>
          </div>
        </div>
      </div>

      <FixedNotesBlock shop={shop} fontSize={13} />
      <SignaturesBlock shop={shop} fontSize={12} />

      <div style={{ marginTop: 40, textAlign: "center", fontSize: 13, fontStyle: "italic", color: "#71717A" }}>
        {shop.bill_footer_text || "Cảm ơn quý khách. Hẹn gặp lại!"}
      </div>
    </div>
  );
}

/** A5 landscape — 210mm x 148mm — compact layout to save paper */
export function InvoiceA5Landscape({ order, shop }) {
  if (!order) return null;
  shop = shop || {};
  const purchaseDate = new Date(order.created_at);
  const printDate = new Date();
  const showQR = shop.bill_show_bank_qr && shop.bank_account && shop.bank_name;

  return (
    <div style={{
      background: "#fff", color: "#000",
      width: "210mm", minHeight: "148mm", height: "auto",
      padding: "5mm 6mm",
      fontFamily: "Times New Roman, serif",
      fontSize: 12,
      display: "flex", flexDirection: "column",
      boxSizing: "border-box",
    }}>
      {/* Compact header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 4, borderBottom: "1.5px solid #2D4A22", marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {shop.bill_show_logo && shop.logo_url && (
            <img src={shop.logo_url} alt="" style={{ width: 28, height: 28, objectFit: "contain" }} />
          )}
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: "#2D4A22" }}>{dc(shop.shop_name)}</div>
            <div style={{ fontSize: 12, color: "#52525B" }}>
              {shop.bill_show_address && <span>{dc(shop.address)} </span>}
              {shop.bill_show_phone && <span>· ĐT: {dc(shop.phone)}</span>}
            </div>
          </div>
        </div>
        <div style={{ textAlign: "right", lineHeight: 1.2 }}>
          <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: 0.5 }}>{shop.bill_invoice_title || "HÓA ĐƠN"}</div>
          <div style={{ fontSize: 12, fontFamily: "Times New Roman, serif" }}>{order.order_code}</div>
        </div>
      </div>

      {/* Customer + dates compact */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 12, lineHeight: 1.3 }}>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 12, textTransform: "uppercase", color: "#71717A" }}>Khách:</span>{" "}
          <strong style={{ fontSize: 19 }}>{order.customer_name}</strong>
          {order.customer_phone && <span style={{ fontSize: 17 }}> · {order.customer_phone}</span>}
          {order.customer_address && <div style={{ fontSize: 12 }}>{order.customer_address}</div>}
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 12 }}>Mua: {purchaseDate.toLocaleString("vi-VN")}</div>
          <div style={{ fontSize: 12 }}>In: {printDate.toLocaleString("vi-VN")}</div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, flex: 1, minHeight: 0 }}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#F4F1EA", textTransform: "uppercase", fontSize: 12, color: "#52525B" }}>
                <th style={{ padding: "3px 4px", textAlign: "left" }}>Tên hàng</th>
                <th style={{ padding: "3px 4px", textAlign: "right", width: "18%" }}>Đơn giá</th>
                <th style={{ padding: "3px 4px", textAlign: "center", width: "8%" }}>SL</th>
                <th style={{ padding: "3px 4px", textAlign: "right", width: "20%" }}>Thành tiền</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((it, i) => (
                <tr key={i} style={{ borderBottom: "1px dotted #E4E4E7" }}>
                  <td style={{ padding: "3px 4px" }}>{it.name}</td>
                  <td style={{ padding: "3px 4px", textAlign: "right", fontFamily: "Times New Roman, serif" }}>{formatVND(it.price)}</td>
                  <td style={{ padding: "3px 4px", textAlign: "center" }}>{it.quantity}</td>
                  <td style={{ padding: "3px 4px", textAlign: "right", fontFamily: "Times New Roman, serif", fontWeight: 600 }}>{formatVND(it.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ marginTop: "auto", paddingTop: 4 }}>
            <div style={{ marginLeft: "auto", width: "55%", fontSize: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "1px 0" }}>
                <span style={{ color: "#52525B" }}>Tạm tính</span>
                <span style={{ fontFamily: "Times New Roman, serif" }}>{formatVND(order.subtotal)}</span>
              </div>
              {(order.discount > 0 || order.discount_percent > 0) && (
                <div style={{ display: "flex", justifyContent: "space-between", padding: "1px 0" }}>
                  <span style={{ color: "#52525B" }}>Giảm{order.discount_percent > 0 ? ` (${order.discount_percent}%)` : ""}</span>
                  <span style={{ fontFamily: "Times New Roman, serif" }}>- {formatVND(order.discount_amount || order.discount)}</span>
                </div>
              )}
              {shop.bill_show_shipping !== false && order.shipping_fee > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", padding: "1px 0" }}>
                  <span style={{ color: "#52525B" }}>Phí ship</span>
                  <span style={{ fontFamily: "Times New Roman, serif" }}>+ {formatVND(order.shipping_fee)}</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0 1px", borderTop: `1.5px solid ${shop.bill_accent_color || "#2D4A22"}`, marginTop: 2, fontSize: 17, fontWeight: 700 }}>
                <span>TỔNG</span>
                <span style={{ color: shop.bill_accent_color || "#2D4A22", fontFamily: "Times New Roman, serif" }}>{formatVND(order.total)}</span>
              </div>
            </div>
          </div>
        </div>

        {showQR && (
          <div style={{ textAlign: "center", minWidth: 90, alignSelf: "center" }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "#71717A", marginBottom: 2 }}>
              QR Thanh toán
            </div>
            <VietQRImage shop={shop} amount={order.total} note={order.order_code} size={80} />
            <div style={{ fontSize: 12, marginTop: 2 }}>{shop.bank_name}</div>
            <div style={{ fontSize: 12, fontFamily: "Times New Roman, serif" }}>{shop.bank_account}</div>
          </div>
        )}
      </div>

      <FixedNotesBlock shop={shop} fontSize={10} />
      <SignaturesBlock shop={shop} fontSize={10} />

      <div style={{ textAlign: "center", marginTop: 3, fontSize: 12, fontStyle: "italic", color: "#71717A", paddingTop: 3, borderTop: "1px dotted #E4E4E7" }}>
        {shop.bill_footer_text || "Cảm ơn quý khách"}
      </div>
    </div>
  );
}

// ─── Shared helper: số tiền bằng chữ ────────────────────────────────────────
function readNumber(n) {
  const u = ["", "một", "hai", "ba", "bốn", "năm", "sáu", "bảy", "tám", "chín"];
  const t = ["mười", "mười một", "mười hai", "mười ba", "mười bốn", "mười lăm", "mười sáu", "mười bảy", "mười tám", "mười chín"];
  if (n === 0) return "không";
  if (n < 10) return u[n];
  if (n < 20) return t[n - 10];
  if (n < 100) return u[Math.floor(n / 10)] + " mươi" + (n % 10 ? " " + (n % 10 === 5 ? "lăm" : u[n % 10]) : "");
  if (n < 1000) return u[Math.floor(n / 100)] + " trăm" + (n % 100 ? " " + readNumber(n % 100) : "");
  if (n < 1000000) return readNumber(Math.floor(n / 1000)) + " nghìn" + (n % 1000 ? " " + readNumber(n % 1000) : "");
  if (n < 1000000000) return readNumber(Math.floor(n / 1000000)) + " triệu" + (n % 1000000 ? " " + readNumber(n % 1000000) : "");
  return readNumber(Math.floor(n / 1000000000)) + " tỷ" + (n % 1000000000 ? " " + readNumber(n % 1000000000) : "");
}
function toWords(amount) {
  if (!amount) return "Không đồng";
  const n = Math.round(amount);
  const s = readNumber(n);
  return s.charAt(0).toUpperCase() + s.slice(1) + " đồng";
}

/** ─── A4 Phiếu Xuất Kho — khớp mẫu PDF ──────────────────────────────────── */
function PhieuXuatKhoSingle({ order, shop, lien = 1 }) {
  if (!order) return null;
  shop = shop || {};
  const purchaseDate = new Date(order.created_at);
  const printDate = new Date();
  const pxkTitle = shop.pxk_title || "Phiếu Xuất Kho Bán Hàng";
  const pxkCreator = shop.pxk_creator || "";
  const pxkWarehouseKeeper = shop.pxk_warehouse_keeper || "";
  const pxkUnit = shop.pxk_default_unit || "Cái";
  const showSku = shop.pxk_show_sku === true || shop.pxk_show_sku === undefined;
  const sigBuyer = shop.pxk_sig_buyer === true || shop.pxk_sig_buyer === undefined;
  const sigWarehouse = shop.pxk_sig_warehouse === true || shop.pxk_sig_warehouse === undefined;
  const sigShipper = shop.pxk_sig_shipper === true || shop.pxk_sig_shipper === undefined;
  const sigCreator = shop.pxk_sig_creator === true || shop.pxk_sig_creator === undefined;
  const showQR = shop.bill_show_bank_qr && shop.bank_account && shop.bank_name;

  const border = "1px solid #000";
  const cellStyle = { border, padding: "4px 6px", fontSize: 13, verticalAlign: "middle" };
  const thStyle = { border, padding: "5px 6px", fontSize: 13, fontWeight: 600, textAlign: "center", background: "#f5f5f5", whiteSpace: "nowrap", verticalAlign: "middle" };
  const colSpanCount = showSku ? 7 : 6;

  return (
    <div style={{
      width: "210mm",
      padding: "12mm 14mm 10mm 18mm",
      fontFamily: "Times New Roman, serif",
      fontSize: 17,
      color: "#000",
      background: "#fff",
      boxSizing: "border-box",
    }}>
      {/* ── HEADER: logo + thông tin cơ sở + QR góc trên phải ── */}
      <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 6 }}>
        {shop.logo_url && (
          <img src={shop.logo_url} alt="" style={{ width: 72, height: 72, objectFit: "contain", marginRight: 12 }} />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 17, color: "#c00", textTransform: "uppercase", marginBottom: 2 }}>
            {dc(shop.shop_name)}
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <div>Đ/C: {dc(shop.address)}</div>
            <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
              <span>MST: {dc(shop.tax_id)}</span>
              <span>Email: {dc(shop.email)}</span>
            </div>
            <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
              <span>Số điện thoại: {dc(shop.phone)}</span>
              <span>Website: {dc(shop.website)}</span>
            </div>
          </div>
        </div>
        {showQR && (
          <div style={{ textAlign: "center", marginLeft: 10, flexShrink: 0 }}>
            <VietQRImage shop={shop} amount={order.total} note={order.order_code} size={96} />
            <div style={{ fontSize: 12, marginTop: 2, fontWeight: 600 }}>{shop.bank_name}</div>
            <div style={{ fontSize: 12, fontFamily: "Times New Roman, serif" }}>{shop.bank_account}</div>
            <div style={{ fontSize: 12, color: "#555" }}>Quét QR thanh toán</div>
          </div>
        )}
      </div>

      {/* ── TIÊU ĐỀ ── */}
      <div style={{ textAlign: "center", marginBottom: 2 }}>
        <div style={{ fontWeight: 700, fontSize: 23, textTransform: "uppercase", letterSpacing: 1 }}>
          {pxkTitle}
        </div>
        <div style={{ fontWeight: 700, fontSize: 17 }}>Số: {order.order_code}</div>
      </div>
      <div style={{ textAlign: "right", fontStyle: "italic", fontSize: 13, marginBottom: 8 }}>
        {lien === 1 ? "Liên 1: Giao cho khách hàng" : lien === 2 ? "Liên 2: Lưu nội bộ" : "Liên 3: Lưu kho"}
      </div>

      {/* ── THÔNG TIN KHÁCH HÀNG ── */}
      <div style={{ fontSize: 19, lineHeight: 1.9, marginBottom: 8 }}>
        <div>
          <span>Tên khách hàng: <strong style={{ fontSize: 22 }}>{order.customer_name || ""}</strong></span>
        </div>
        <div>Địa chỉ: {dc(order.customer_address)}</div>
        <div style={{ display: "flex", gap: 40 }}>
          <span>Điện thoại: {dc(order.customer_phone)}</span>
        </div>
        {shop.pxk_show_seller !== false && (
          <div>NV Bán hàng: <span style={{ borderBottom: "1px dotted #000", display: "inline-block", minWidth: 200 }}>&nbsp;</span></div>
        )}
      </div>

      {/* ── BẢNG HÀNG HÓA ── */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 6, fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: "5%" }}>STT</th>
            {showSku && <th style={{ ...thStyle, width: "10%" }}>Mã hàng</th>}
            <th style={{ ...thStyle }}>Tên hàng</th>
            <th style={{ ...thStyle, width: "9%" }}>Đơn vị tính</th>
            <th style={{ ...thStyle, width: "8%" }}>Số lô</th>
            <th style={{ ...thStyle, width: "11%" }}>Hạn sử dụng</th>
            <th style={{ ...thStyle, width: "8%" }}>Số lượng</th>
            <th style={{ ...thStyle, width: "13%" }}>Đơn giá</th>
            <th style={{ ...thStyle, width: "13%" }}>Thành tiền</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((it, i) => (
            <tr key={i}>
              <td style={{ ...cellStyle, textAlign: "center" }}>{i + 1}</td>
              {showSku && <td style={{ ...cellStyle, textAlign: "center" }}>{it.sku || it.product_code || ""}</td>}
              <td style={{ ...cellStyle }}>{it.name}</td>
              <td style={{ ...cellStyle, textAlign: "center" }}>{it.unit || pxkUnit}</td>
              <td style={{ ...cellStyle, textAlign: "center" }}>{it.lot_number || ""}</td>
              <td style={{ ...cellStyle, textAlign: "center" }}>{it.expiry_date || ""}</td>
              <td style={{ ...cellStyle, textAlign: "center" }}>{it.quantity}</td>
              <td style={{ ...cellStyle, textAlign: "right", fontFamily: "Times New Roman, serif" }}>{formatVND(it.price)}</td>
              <td style={{ ...cellStyle, textAlign: "right", fontFamily: "Times New Roman, serif" }}>{formatVND(it.subtotal)}</td>
            </tr>
          ))}
          {/* Dòng trống nếu ít hàng */}
          {order.items.length < 3 && Array.from({ length: 3 - order.items.length }).map((_, i) => (
            <tr key={"blank-" + i}>
              <td style={{ ...cellStyle, height: 24 }}>&nbsp;</td>
              {showSku && <td style={cellStyle}></td>}
              <td style={cellStyle}></td>
              <td style={cellStyle}></td>
              <td style={cellStyle}></td>
              <td style={cellStyle}></td>
              <td style={cellStyle}></td>
              <td style={cellStyle}></td>
              <td style={cellStyle}></td>
            </tr>
          ))}
          {/* Tổng số lượng + Tổng cộng */}
          <tr>
            <td colSpan={showSku ? 6 : 5} style={{ ...cellStyle, textAlign: "center", fontStyle: "italic", color: "#555" }}>
              Tổng số lượng:
            </td>
            <td style={{ ...cellStyle, textAlign: "center", fontWeight: 700 }}>
              {order.items.reduce((s, it) => s + (it.quantity || 0), 0)}
            </td>
            <td colSpan={2} style={{ ...cellStyle }}></td>
          </tr>
          {shop.bill_show_discount !== false && (
            <tr>
              <td colSpan={showSku ? 8 : 7} style={{ ...cellStyle, textAlign: "center" }}>
                Chiết khấu bán hàng{order.discount_percent > 0 ? ` (${order.discount_percent}%)` : ""} (2)
              </td>
              <td style={{ ...cellStyle, textAlign: "right", fontFamily: "Times New Roman, serif" }}>
                {formatVND(order.discount_amount || order.discount || 0)}
              </td>
            </tr>
          )}
          {shop.bill_show_discount !== false && shop.bill_show_after_discount !== false && (
            <tr>
              <td colSpan={showSku ? 8 : 7} style={{ ...cellStyle, textAlign: "center" }}>Sau chiết khấu (3) (3 = 1 - 2)</td>
              <td style={{ ...cellStyle, textAlign: "right", fontFamily: "Times New Roman, serif" }}>
                {formatVND((order.subtotal || 0) - (order.discount_amount || order.discount || 0))}
              </td>
            </tr>
          )}
          {shop.bill_show_shipping !== false && (order.shipping_fee > 0) && (
            <tr>
              <td colSpan={showSku ? 8 : 7} style={{ ...cellStyle, textAlign: "center" }}>Phí vận chuyển (4)</td>
              <td style={{ ...cellStyle, textAlign: "right", fontFamily: "Times New Roman, serif" }}>
                {formatVND(order.shipping_fee)}
              </td>
            </tr>
          )}
          <tr>
            <td colSpan={showSku ? 8 : 7} style={{ ...cellStyle, textAlign: "center", fontWeight: 700 }}>
              Tổng tiền thanh toán
            </td>
            <td style={{ ...cellStyle, textAlign: "right", fontFamily: "Times New Roman, serif", fontWeight: 700 }}>{formatVND(order.total)}</td>
          </tr>
        </tbody>
      </table>

      {/* ── FOOTER: giữ nguyên tờ, không bị tách sang trang 2 ── */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>

      {/* ── SỐ TIỀN BẰNG CHỮ ── */}
      <div style={{ fontSize: 13, marginBottom: 5 }}>
        <strong>Số tiền viết bằng chữ:</strong> {toWords(order.total)}
      </div>

      {/* ── HÌNH THỨC THANH TOÁN ── */}
      <div style={{ fontSize: 13, marginBottom: 12 }}>
        <strong>Hình thức thanh toán:</strong>{" "}
        Tiền mặt □ &nbsp;-&nbsp; Công nợ □ &nbsp;-&nbsp; Chuyển khoản □
      </div>

      {/* ── QR + CHỮ KÝ ── */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, marginTop: 8 }}>
        {/* Ô chữ ký */}
        <div style={{ flex: 1, display: "flex", justifyContent: "space-around", gap: 4 }}>
          {sigBuyer && (
            <div style={{ textAlign: "center", flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>Người mua hàng</div>
              <div style={{ fontStyle: "italic", fontSize: 12 }}>(Ký, họ tên)</div>
              <div style={{ height: 52 }}></div>
            </div>
          )}
          {sigWarehouse && (
            <div style={{ textAlign: "center", flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>Thủ kho</div>
              {pxkWarehouseKeeper && (
                <div style={{ fontSize: 12, fontWeight: 600 }}>{pxkWarehouseKeeper}</div>
              )}
              <div style={{ fontStyle: "italic", fontSize: 12 }}>(Ký, họ tên)</div>
              <div style={{ height: 52 }}></div>
            </div>
          )}
          {sigShipper && (
            <div style={{ textAlign: "center", flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>Người giao hàng</div>
              {order.assigned_shipper_name && (
                <div style={{ fontSize: 12, fontWeight: 600 }}>{order.assigned_shipper_name}</div>
              )}
              <div style={{ fontStyle: "italic", fontSize: 12 }}>(Ký, họ tên)</div>
              <div style={{ height: 52 }}></div>
            </div>
          )}
          {sigCreator && (
            <div style={{ textAlign: "center", flex: 1 }}>
              <div style={{ fontStyle: "italic", fontSize: 12, marginBottom: 2 }}>
                Ngày {printDate.getDate()} tháng {printDate.getMonth() + 1} năm {printDate.getFullYear()}
              </div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>Người lập phiếu</div>
              {pxkCreator && <div style={{ fontSize: 12 }}>{pxkCreator}</div>}
              <div style={{ fontStyle: "italic", fontSize: 12 }}>(Ký, họ tên, đóng dấu)</div>
              <div style={{ height: 52 }}></div>
            </div>
          )}
        </div>
      </div>
      </div> {/* end footer avoid-break */}
    </div>
  );
}

/** ─── A5 Phiếu Xuất Kho (148mm × 210mm portrait, nhỏ gọn) ─────────────────── */
function PhieuXuatKhoA5Single({ order, shop, lien = 1 }) {
  if (!order) return null;
  shop = shop || {};
  const purchaseDate = new Date(order.created_at);
  const printDate = new Date();
  const pxkTitle = shop.pxk_title || "Phiếu Xuất Kho Bán Hàng";
  const pxkCreator = shop.pxk_creator || "";
  const pxkWarehouseKeeper = shop.pxk_warehouse_keeper || "";
  const pxkUnit = shop.pxk_default_unit || "Cái";
  const showSku = shop.pxk_show_sku === true || shop.pxk_show_sku === undefined;
  const sigBuyer = shop.pxk_sig_buyer === true || shop.pxk_sig_buyer === undefined;
  const sigWarehouse = shop.pxk_sig_warehouse === true || shop.pxk_sig_warehouse === undefined;
  const sigShipper = shop.pxk_sig_shipper === true || shop.pxk_sig_shipper === undefined;
  const sigCreator = shop.pxk_sig_creator === true || shop.pxk_sig_creator === undefined;
  const showQR = shop.bill_show_bank_qr && shop.bank_account && shop.bank_name;

  const border = "1px solid #000";
  const cell = { border, padding: "2px 4px", fontSize: 12.5 };
  const th = { border, padding: "3px 4px", fontSize: 12.5, fontWeight: 600, textAlign: "center", background: "#f5f5f5", whiteSpace: "nowrap", verticalAlign: "middle" };

  // A5 NGANG: 210mm × 148mm
  return (
    <div className="pxk-a5l-page" style={{
      width: "210mm",
      padding: "4mm 8mm 4mm 10mm",
      fontFamily: "Times New Roman, serif",
      fontSize: 12.5,
      color: "#000",
      background: "#fff",
      boxSizing: "border-box",
    }}>

      {/* ── HEADER: logo + thông tin bên trái, QR bên phải ── */}
      <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 2, gap: 8 }}>

        {/* Logo */}
        {shop.logo_url && (
          <img src={shop.logo_url} alt="" style={{ width: 44, height: 44, objectFit: "contain", flexShrink: 0 }} />
        )}

        {/* Thông tin cơ sở */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13.5, color: "#c00", textTransform: "uppercase" }}>
            {dc(shop.shop_name)}
          </div>
          <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
            <div>Đ/C: {dc(shop.address)}</div>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              <span>MST: {dc(shop.tax_id)}</span>
              <span>Email: {dc(shop.email)}</span>
            </div>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              <span>Số điện thoại: {dc(shop.phone)}</span>
              <span>Website: {dc(shop.website)}</span>
            </div>
          </div>
        </div>

        {/* QR góc trên phải */}
        {showQR && (
          <div style={{ textAlign: "center", flexShrink: 0 }}>
            <VietQRImage shop={shop} amount={order.total} note={order.order_code} size={64} />
            <div style={{ fontSize: 10.5, marginTop: 1, fontWeight: 600 }}>{shop.bank_name}</div>
            <div style={{ fontSize: 10.5, fontFamily: "Times New Roman, serif" }}>{shop.bank_account}</div>
            <div style={{ fontSize: 10.5, color: "#666" }}>Quét QR TT</div>
          </div>
        )}
      </div>

      {/* ── TIÊU ĐỀ căn giữa toàn trang ── */}
      <div style={{ textAlign: "center", marginBottom: 4 }}>
        <div style={{ fontWeight: 700, fontSize: 17.5, textTransform: "uppercase", letterSpacing: 0.5 }}>{pxkTitle}</div>
        <div style={{ fontWeight: 700, fontSize: 12.5 }}>Số: {order.order_code}</div>
        <div style={{ fontStyle: "italic", fontSize: 12.5, marginTop: 1 }}>
          {lien === 1 ? "Liên 1: Giao cho khách hàng" : lien === 2 ? "Liên 2: Lưu nội bộ" : "Liên 3: Lưu kho"}
        </div>
      </div>

      {/* ── THÔNG TIN KHÁCH HÀNG ── */}
      <div style={{ fontSize: 12.5, lineHeight: 1.5, marginBottom: 3 }}>
        <div>
          <span style={{ flex: 1 }}>Tên khách hàng: <strong style={{ fontSize: 13.5 }}>{order.customer_name || ""}</strong></span>
        </div>
        <div style={{ display: "flex", gap: 24 }}>
          <span style={{ flex: 1 }}>Địa chỉ: {order.customer_address || ""}</span>
          <span>Điện thoại: {order.customer_phone || ""}</span>
        </div>
        <div style={{ display: "flex", gap: 24 }}>
        </div>
        <div style={{ display: "flex", gap: 24 }}>
          {shop.pxk_show_seller !== false && (
            <span>NV Bán hàng: <span style={{ borderBottom: "1px dotted #000", display: "inline-block", minWidth: 80 }}>&nbsp;</span></span>
          )}
        </div>
      </div>

      {/* ── BẢNG HÀNG HÓA ── */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 3, fontSize: 12.5 }}>
        <thead>
          <tr>
            <th style={{ ...th, width: "4%" }}>STT</th>
            {showSku && <th style={{ ...th, width: "9%" }}>Mã hàng</th>}
            <th style={th}>Tên hàng</th>
            <th style={{ ...th, width: "7%" }}>ĐVT</th>
            <th style={{ ...th, width: "7%" }}>Số lô</th>
            <th style={{ ...th, width: "9%" }}>HSD</th>
            <th style={{ ...th, width: "6%" }}>SL</th>
            <th style={{ ...th, width: "12%" }}>Đơn giá</th>
            <th style={{ ...th, width: "12%" }}>Thành tiền</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((it, i) => (
            <tr key={i}>
              <td style={{ ...cell, textAlign: "center" }}>{i + 1}</td>
              {showSku && <td style={{ ...cell, textAlign: "center" }}>{it.sku || it.product_code || ""}</td>}
              <td style={cell}>{it.name}</td>
              <td style={{ ...cell, textAlign: "center" }}>{it.unit || pxkUnit}</td>
              <td style={{ ...cell, textAlign: "center" }}>{it.lot_number || ""}</td>
              <td style={{ ...cell, textAlign: "center" }}>{it.expiry_date || ""}</td>
              <td style={{ ...cell, textAlign: "center" }}>{it.quantity}</td>
              <td style={{ ...cell, textAlign: "right", fontFamily: "Times New Roman, serif" }}>{formatVND(it.price)}</td>
              <td style={{ ...cell, textAlign: "right", fontFamily: "Times New Roman, serif" }}>{formatVND(it.subtotal)}</td>
            </tr>
          ))}
          {order.items.length < 3 && Array.from({ length: 3 - order.items.length }).map((_, i) => (
            <tr key={"b" + i}>
              <td style={{ ...cell, height: 16 }}>&nbsp;</td>
              {showSku && <td style={cell}></td>}
              <td style={cell}></td><td style={cell}></td><td style={cell}></td>
              <td style={cell}></td><td style={cell}></td><td style={cell}></td><td style={cell}></td>
            </tr>
          ))}
          <tr>
            <td colSpan={showSku ? 6 : 5} style={{ ...cell, textAlign: "center", fontStyle: "italic", color: "#555" }}>
              Tổng số lượng:
            </td>
            <td style={{ ...cell, textAlign: "center", fontWeight: 700 }}>
              {order.items.reduce((s, it) => s + (it.quantity || 0), 0)}
            </td>
            <td colSpan={2} style={{ ...cell }}></td>
          </tr>
          {shop.bill_show_discount !== false && (
            <tr>
              <td colSpan={showSku ? 8 : 7} style={{ ...cell, textAlign: "center" }}>
                Chiết khấu bán hàng{order.discount_percent > 0 ? ` (${order.discount_percent}%)` : ""} (2)
              </td>
              <td style={{ ...cell, textAlign: "right", fontFamily: "Times New Roman, serif" }}>
                {formatVND(order.discount_amount || order.discount || 0)}
              </td>
            </tr>
          )}
          {shop.bill_show_discount !== false && shop.bill_show_after_discount !== false && (
            <tr>
              <td colSpan={showSku ? 8 : 7} style={{ ...cell, textAlign: "center" }}>Sau chiết khấu (3) (3 = 1 - 2)</td>
              <td style={{ ...cell, textAlign: "right", fontFamily: "Times New Roman, serif" }}>
                {formatVND((order.subtotal || 0) - (order.discount_amount || order.discount || 0))}
              </td>
            </tr>
          )}
          {shop.bill_show_shipping !== false && (order.shipping_fee > 0) && (
            <tr>
              <td colSpan={showSku ? 8 : 7} style={{ ...cell, textAlign: "center" }}>Phí vận chuyển (4)</td>
              <td style={{ ...cell, textAlign: "right", fontFamily: "Times New Roman, serif" }}>
                {formatVND(order.shipping_fee)}
              </td>
            </tr>
          )}
          <tr>
            <td colSpan={showSku ? 8 : 7} style={{ ...cell, textAlign: "center", fontWeight: 700 }}>
              Tổng tiền thanh toán
            </td>
            <td style={{ ...cell, textAlign: "right", fontFamily: "Times New Roman, serif", fontWeight: 700 }}>{formatVND(order.total)}</td>
          </tr>
        </tbody>
      </table>

      {/* ── TIỀN BẰNG CHỮ + THANH TOÁN ── */}
      <div style={{ fontSize: 12.5, marginBottom: 2 }}>
        <strong>Số tiền viết bằng chữ:</strong> {toWords(order.total)}
      </div>
      <div style={{ fontSize: 12.5, marginBottom: 4 }}>
        <strong>Hình thức thanh toán:</strong> Tiền mặt □ &nbsp;-&nbsp; Công nợ □ &nbsp;-&nbsp; Chuyển khoản □
      </div>

      {/* ── CHỮ KÝ ── */}
      <div style={{ display: "flex", justifyContent: "space-around", gap: 4 }}>
        {sigBuyer && (
          <div style={{ textAlign: "center", flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 12.5 }}>Người mua hàng</div>
            <div style={{ fontStyle: "italic", fontSize: 12.5 }}>(Ký, họ tên)</div>
            <div style={{ height: 18 }}></div>
          </div>
        )}
        {sigWarehouse && (
          <div style={{ textAlign: "center", flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 12.5 }}>Thủ kho</div>
            {pxkWarehouseKeeper && (
              <div style={{ fontSize: 12.5, fontWeight: 600 }}>{pxkWarehouseKeeper}</div>
            )}
            <div style={{ fontStyle: "italic", fontSize: 12.5 }}>(Ký, họ tên)</div>
            <div style={{ height: 18 }}></div>
          </div>
        )}
        {sigShipper && (
          <div style={{ textAlign: "center", flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 12.5 }}>Người giao hàng</div>
            {order.assigned_shipper_name && (
              <div style={{ fontSize: 12.5, fontWeight: 600 }}>{order.assigned_shipper_name}</div>
            )}
            <div style={{ fontStyle: "italic", fontSize: 12.5 }}>(Ký, họ tên)</div>
            <div style={{ height: 18 }}></div>
          </div>
        )}
        {sigCreator && (
          <div style={{ textAlign: "center", flex: 1 }}>
            <div style={{ fontStyle: "italic", fontSize: 12.5, marginBottom: 1 }}>
              Ngày {printDate.getDate()} tháng {printDate.getMonth() + 1} năm {printDate.getFullYear()}
            </div>
            <div style={{ fontWeight: 700, fontSize: 12.5 }}>Người lập phiếu</div>
            {pxkCreator && <div style={{ fontSize: 12.5 }}>{pxkCreator}</div>}
            <div style={{ fontStyle: "italic", fontSize: 12.5 }}>(Ký, họ tên, đóng dấu)</div>
            <div style={{ height: 18 }}></div>
          </div>
        )}
      </div>
    </div>
  );
}

/** ─── Phiếu Xuất Kho Nhiệt 80mm ─────────────────────────────────────────── */
export function PhieuXuatKhoThermal({ order, shop }) {
  if (!order) return null;
  shop = shop || {};
  const purchaseDate = new Date(order.created_at);
  const printDate = new Date();
  const pxkTitle = shop.pxk_title || "Phiếu Xuất Kho";
  const pxkUnit = shop.pxk_default_unit || "Cái";
  const showQR = shop.bill_show_bank_qr && shop.bank_account && shop.bank_name;

  return (
    <div className="thermal-80mm" style={{ padding: "3mm 4mm" }}>
      {/* Header cơ sở */}
      <div style={{ textAlign: "center", marginBottom: 5 }}>
        {shop.logo_url && (
          <img src={shop.logo_url} alt="" style={{ width: 36, height: 36, objectFit: "contain", marginBottom: 3 }} />
        )}
        <div style={{ fontSize: 13, fontWeight: 700 }}>{dc(shop.shop_name)}</div>
        <div style={{ fontSize: 10 }}>{dc(shop.address)}</div>
        <div style={{ fontSize: 10 }}>ĐT: {dc(shop.phone)}</div>
        <div style={{ fontSize: 10 }}>STK: {dc(shop.bank_account)} ({dc(shop.bank_name)})</div>
        <div style={{ fontSize: 12, fontWeight: 700, marginTop: 4, textTransform: "uppercase" }}>{pxkTitle}</div>
      </div>

      <div className="divider" />

      {/* Thông tin đơn */}
      <div style={{ fontSize: 12, lineHeight: 1.6 }}>
        <div className="row"><span>Số phiếu:</span><strong>{order.order_code}</strong></div>
        <div className="row">
          <span>Ngày:</span>
          <span>{purchaseDate.getDate()}/{purchaseDate.getMonth() + 1}/{purchaseDate.getFullYear()}</span>
        </div>
        <div className="row"><span>Khách:</span><strong style={{ maxWidth: "60%", textAlign: "right", fontSize: 17 }}>{order.customer_name}</strong></div>
        {order.customer_phone && <div className="row"><span>SĐT:</span><span>{order.customer_phone}</span></div>}
        {order.customer_address && <div style={{ fontSize: 10, marginTop: 1 }}>Địa chỉ: {order.customer_address}</div>}
      </div>

      <div className="divider" />

      {/* Bảng hàng hóa */}
      <table style={{ width: "100%", fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left" }}>Tên hàng</th>
            <th style={{ textAlign: "center", width: "10mm" }}>SL</th>
            <th style={{ textAlign: "right" }}>Đơn giá</th>
            <th style={{ textAlign: "right", width: "20mm" }}>T.Tiền</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((it, i) => (
            <React.Fragment key={i}>
              <tr><td colSpan={4} style={{ paddingTop: 2, fontWeight: 600 }}>{it.name}</td></tr>
              <tr>
                <td style={{ paddingLeft: 4, fontSize: 10 }}>{it.unit || pxkUnit}{it.lot_number ? ` · Lô: ${it.lot_number}` : ""}</td>
                <td style={{ textAlign: "center" }}>{it.quantity}</td>
                <td style={{ textAlign: "right" }}>{formatVND(it.price)}</td>
                <td style={{ textAlign: "right" }}>{formatVND(it.subtotal)}</td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>

      <div className="divider" />

      {/* Tổng tiền */}
      <div className="row"><span>Tạm tính (1)</span><span>{formatVND(order.subtotal)}</span></div>
      {shop.bill_show_discount !== false && (order.discount > 0 || order.discount_percent > 0) && (
        <div className="row">
          <span>Chiết khấu{order.discount_percent > 0 ? ` (${order.discount_percent}%)` : ""} (2)</span>
          <span>- {formatVND(order.discount_amount || order.discount)}</span>
        </div>
      )}
      {shop.bill_show_discount !== false && shop.bill_show_after_discount !== false && (order.discount > 0 || order.discount_percent > 0) && (
        <div className="row">
          <span>Sau CK (3)</span>
          <span>{formatVND((order.subtotal || 0) - (order.discount_amount || order.discount || 0))}</span>
        </div>
      )}
      {shop.bill_show_shipping !== false && order.shipping_fee > 0 && (
        <div className="row"><span>Phí ship</span><span>+ {formatVND(order.shipping_fee)}</span></div>
      )}
      <div className="divider" />
      <div className="row" style={{ fontSize: 13, fontWeight: 700 }}>
        <span>TỔNG TIỀN TT</span><span>{formatVND(order.total)}</span>
      </div>
      <div style={{ fontSize: 10, marginTop: 3 }}>
        Bằng chữ: {toWords(order.total)}
      </div>

      <div className="divider" />

      {/* Hình thức TT */}
      <div style={{ fontSize: 12, marginBottom: 4 }}>
        TT: Tiền mặt □ &nbsp; Công nợ □ &nbsp; CK □
      </div>

      {/* Ngày in */}
      <div style={{ fontSize: 10, marginBottom: 3 }}>
        Ngày in: {printDate.toLocaleString("vi-VN")}
      </div>

      {/* QR */}
      {showQR && (
        <>
          <div className="divider" />
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 3 }}>QUÉT QR THANH TOÁN</div>
            <VietQRImage shop={shop} amount={order.total} note={order.order_code} size={130} />
            <div style={{ fontSize: 10, marginTop: 3 }}>{shop.bank_name} · {shop.bank_account}</div>
            {shop.bank_account_holder && <div style={{ fontSize: 10 }}>{shop.bank_account_holder}</div>}
          </div>
        </>
      )}

      {/* Ô ký */}
      <div className="divider" />
      <div style={{ display: "flex", justifyContent: "space-around", fontSize: 10, marginTop: 2 }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontWeight: 700 }}>Người mua</div>
          <div style={{ fontStyle: "italic" }}>(Ký, họ tên)</div>
          <div style={{ height: 30 }}></div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontWeight: 700 }}>Giao hàng</div>
          <div style={{ fontStyle: "italic" }}>(Ký, họ tên)</div>
          <div style={{ height: 30 }}></div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontWeight: 700 }}>Lập phiếu</div>
          <div style={{ fontStyle: "italic" }}>(Ký, đóng dấu)</div>
          <div style={{ height: 30 }}></div>
        </div>
      </div>

      <div style={{ textAlign: "center", marginTop: 6, fontSize: 12, fontStyle: "italic" }}>
        — {shop.bill_footer_text || "Cảm ơn quý khách"} —
      </div>
    </div>
  );
}

/** ─── PhieuXuatKho: xuất đúng khổ theo shop.pxk_paper_size ──────────────── */
export function PhieuXuatKho({ order, shop }) {
  if (!order) return null;
  const size = (shop && shop.pxk_paper_size) || "a4";
  const copies = (shop && shop.bill_print_copies) || 1;

  if (size === "thermal") {
    return <PhieuXuatKhoThermal order={order} shop={shop} />;
  }

  if (size === "a5") {
    return (
      <div style={{ background: "#fff" }}>
        <div className={copies >= 2 ? "page-break-after page-break-avoid" : "page-break-avoid"}>
          <PhieuXuatKhoA5Single order={order} shop={shop} lien={1} />
        </div>
        {copies >= 2 && (
          <div className="page-break-avoid">
            <PhieuXuatKhoA5Single order={order} shop={shop} lien={2} />
          </div>
        )}
      </div>
    );
  }

  // Mặc định A4
  return (
    <div style={{ background: "#fff" }}>
      <div className={copies >= 2 ? "page-break-after" : ""}>
        <PhieuXuatKhoSingle order={order} shop={shop} lien={1} />
      </div>
      {copies >= 2 && (
        <div>
          <PhieuXuatKhoSingle order={order} shop={shop} lien={2} />
        </div>
      )}
    </div>
  );
}